import { action, observable, makeObservable } from 'mobx';
import { debounce, Disposers } from '../../../../../../lib/helpers';
import { TimeRange } from '../models';
import { isTimeRangesEqual } from '../utils';

function getDuration(video: HTMLVideoElement | null): number {
  if (video === null)
    return 0;
  // if video is not loaded, NaN
  // if video is streaming, infinity
  if (isFinite(video.duration))
    return video.duration;
  else
    return 0;
}

export class VideoElementStore {
  @observable.ref
  element: HTMLVideoElement | null = null;

  @observable.ref
  buffered: readonly TimeRange[] = [];

  @observable
  duration = 0;

  @observable
  currentTime = 0;

  @observable
  paused = true;

  @observable
  ended = false;

  @observable
  muted = true;

  @observable
  playCount = 0;

  @observable
  playFrom = 0;

  @observable
  isVideoError = false;

  private readonly disposers = new Disposers();

  @action
  setElement = (element: HTMLVideoElement) => {
    this.element = element;
    this.buffered = [];

    this.addEventListeners(element);
    this.disposers.add(this.removeEventListeners(element));
  };

  private addEventListeners = (element: HTMLVideoElement) => {
    element.addEventListener('loadedmetadata', this.setDuration);
    element.addEventListener('durationchange', this.setDuration);
    element.addEventListener('progress', this.handleVideoDownloadProgressUpdate);
    element.addEventListener('timeupdate', this.debouncedOnTimeUpdate);
    element.addEventListener('pause', this.updatePausedOrEnded);
    element.addEventListener('play', this.updatePausedOrEnded);
    element.addEventListener('playing', this.updatePausedOrEnded);
    element.addEventListener('waiting', this.updatePausedOrEnded);
    element.addEventListener('ended', this.updatePausedOrEnded);
  };

  private removeEventListeners = (element: HTMLVideoElement) => () => {
    element.removeEventListener('loadedmetadata', this.setDuration);
    element.removeEventListener('durationchange', this.setDuration);
    element.removeEventListener('progress', this.handleVideoDownloadProgressUpdate);
    element.removeEventListener('timeupdate', this.debouncedOnTimeUpdate);
    element.removeEventListener('pause', this.updatePausedOrEnded);
    element.removeEventListener('play', this.updatePausedOrEnded);
    element.removeEventListener('playing', this.updatePausedOrEnded);
    element.removeEventListener('waiting', this.updatePausedOrEnded);
    element.removeEventListener('ended', this.updatePausedOrEnded);
  };

  @action
  setCurrentTime = (currentTime: number) => {
    if (this.element === null)
      return;
    this.element.currentTime = currentTime;
    if (!this.paused) {
      this.playFrom = currentTime;
      this.playCount++;
    }
  };

  @action
  setIsVideoError = (value: boolean) => {
    this.isVideoError = value;
  };

  @action
  togglePlay = () => {
    if (this.element === null)
      return;
    if (this.paused || this.ended)
      this.play();
    else
      this.pause();
  };

  @action
  play = () => {
    if (this.element === null)
      return;
    if (this.ended)
      this.setCurrentTime(0);
    this.playFrom = this.currentTime;
    this.playCount++;
    this.element.play();
  };

  @action
  pause = () => {
    if (this.element === null)
      return;
    this.element.pause();
  };

  @action
  toggleVolume = () => {
    if (this.element === null)
      return;
    this.element.muted = !this.element.muted;
    this.muted = this.element.muted;
  };

  @action
  private setDuration = () => {
    if (this.duration !== 0)
      return;
    this.duration = getDuration(this.element);
  };

  @action
  private handleVideoDownloadProgressUpdate = (): void => {
    const video = this.element;
    if (video === null) {
      return;
    }
    const progress: TimeRange[] = [];
    for (let i = 0; i < video.buffered.length; i++) {
      const start: number = video.buffered.start(i);
      const end: number = video.buffered.end(i);
      progress.push({ start: start, end: end });
    }
    if (isTimeRangesEqual(this.buffered, progress))
      return;
    this.buffered = progress;
  }

  @action
  private changeCurrentTime = (time: number) => {
    this.currentTime = time;
  }

  @action
  private onTimeUpdate = () => {
    const video = this.element;
    if (video === null) {
      this.changeCurrentTime(0);
    }
    else {
      this.changeCurrentTime(video.currentTime);
    }
  };

  private debouncedOnTimeUpdate = debounce(this.onTimeUpdate, 200);

  @action
  private updatePausedOrEnded = () => {
    this.paused = this.element!.paused;
    this.ended = this.element!.ended;
  };

  constructor() {
    makeObservable(this);
  }

  dispose() {
    this.disposers.dispose();
  }
}
